import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { emailLogAPI } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../utils/errorMessage';
import { EmailCategory, EMAIL_CATEGORY_LABELS } from '../types/emailLog';
import type { EmailLogEntry } from '../types/emailLog';

const PAGE_SIZE = 50;

const formatDateTime = (value: string) => new Date(value).toLocaleString('hu-HU');

/**
 * A kiküldött emailek naplója. Minden kimenő levél bekerül ide - a meghívóktól
 * a heti összefoglalóig -, a sikertelenek a hiba szövegével együtt.
 *
 * A levelek törzsét nem tároljuk: ideiglenes jelszavak és meghívó linkek
 * vannak bennük.
 */
const EmailLogPage: React.FC = () => {
  usePageTitle('Email napló');
  const { showToast } = useToast();

  const [entries, setEntries] = useState<EmailLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [failedTotal, setFailedTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await emailLogAPI.getAll({
        search: search.trim() || undefined,
        category: category === '' ? undefined : (Number(category) as EmailCategory),
        onlyFailed: onlyFailed || undefined,
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setEntries(result.items);
      setTotal(result.total);
      setFailedTotal(result.failedTotal);
    } catch (err) {
      showToast(getErrorMessage(err, 'Az email naplót nem sikerült betölteni.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [search, category, onlyFailed, page, showToast]);

  useEffect(() => {
    // Gépelés közben ne indítsunk kérést minden karakterre
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // Szűrő váltásakor vissza az első oldalra, különben üres lapon lehet kikötni
  useEffect(() => {
    setPage(0);
  }, [search, category, onlyFailed]);

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email napló</h1>
          <p className="mt-1 text-sm text-gray-500">
            A rendszerből kiküldött összes levél. {total} találat
            {failedTotal > 0 && ` · összesen ${failedTotal} sikertelen küldés`}.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Frissítés
        </button>
      </div>

      <div className="flex flex-col gap-3 rounded-lg bg-white p-4 shadow md:flex-row md:items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Keresés címzett vagy tárgy szerint"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 md:max-w-sm"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 md:w-auto"
        >
          <option value="">Minden típus</option>
          {Object.entries(EMAIL_CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Csak a sikertelenek
        </label>
      </div>

      {loading ? (
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      ) : entries.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
          Nincs a szűrésnek megfelelő levél.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <ul className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 p-4">
                {entry.isSuccess ? (
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                ) : (
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="break-all text-sm font-medium text-gray-900">
                      {entry.toEmail}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {EMAIL_CATEGORY_LABELS[entry.category] ?? 'Egyéb'}
                    </span>
                  </div>
                  <p className="mt-0.5 break-words text-sm text-gray-600">{entry.subject}</p>
                  <p className="mt-0.5 text-xs text-gray-400">{formatDateTime(entry.sentAt)}</p>
                  {entry.error && (
                    <p className="mt-1 break-words rounded-md bg-red-50 p-2 text-xs text-red-700">
                      {entry.error}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastPage > 0 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Előző
          </button>
          <span className="text-sm text-gray-500">
            {page + 1} / {lastPage + 1}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            disabled={page >= lastPage}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Következő
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailLogPage;
